import { useEffect, useRef } from 'react';
import { useRouter } from 'next/router';
import { useConsent } from '../../consent/ConsentContext';
import { isCodeRoute, isTokenBearingRoute } from '../../util/redactUrl';

/**
 * Consent-gated loader for sitewide third-party scripts (GDPR plan P2-5):
 *   - Cloudflare Insights (RUM beacon) → analytics consent
 *   - Supademo (interactive product demos) → marketing consent
 *   - LinkedIn Insight Tag (audience collection) → marketing consent
 *   - Meta Pixel (audience collection + page views) → marketing consent
 *
 * They inject ONLY after the matching category is granted. We don't remove an
 * already-injected script on withdrawal within the same page (these providers
 * have no clean teardown); because the tags are no longer rendered server-side,
 * the next navigation/reload starts clean and re-checks consent.
 */

const CF_BEACON_TOKEN = 'ade3dd32934f455facaf089410f16806';
const LINKEDIN_PARTNER_ID = '10040812';
const META_PIXEL_ID = '1772996420752283';

function injectScript(id, attrs) {
  if (typeof document === 'undefined') return;
  if (document.getElementById(id)) return;
  const s = document.createElement('script');
  s.id = id;
  Object.entries(attrs).forEach(([k, v]) => {
    if (v === true || v === '') s.setAttribute(k, '');
    else s.setAttribute(k, v);
  });
  document.body.appendChild(s);
}

export default function ConsentedScripts() {
  const { asPath, pathname } = useRouter();
  const { has } = useConsent();
  const analytics = has('analytics');
  const marketing = has('marketing');
  const lastMetaPageView = useRef(null);
  const path = asPath.split(/[?#]/)[0];
  const tokenPage =
    isTokenBearingRoute(pathname) || isTokenBearingRoute(path) || isCodeRoute(path);

  useEffect(() => {
    if (!analytics) return;
    injectScript('cf-beacon', {
      src: 'https://static.cloudflareinsights.com/beacon.min.js',
      defer: true,
      'data-cf-beacon': JSON.stringify({ token: CF_BEACON_TOKEN }),
    });
  }, [analytics]);

  useEffect(() => {
    if (!marketing) return;
    injectScript('supademo-sdk', {
      src: 'https://script.supademo.com/script.js',
    });
  }, [marketing]);

  useEffect(() => {
    if (!marketing || tokenPage || typeof window === 'undefined') return;

    window._linkedin_partner_id = LINKEDIN_PARTNER_ID;
    window._linkedin_data_partner_ids =
      window._linkedin_data_partner_ids || [];
    if (!window._linkedin_data_partner_ids.includes(LINKEDIN_PARTNER_ID)) {
      window._linkedin_data_partner_ids.push(LINKEDIN_PARTNER_ID);
    }
    if (!window.lintrk) {
      window.lintrk = function lintrk(...args) {
        window.lintrk.q.push(args);
      };
      window.lintrk.q = [];
    }

    injectScript('linkedin-insight-tag', {
      src: 'https://snap.licdn.com/li.lms-analytics/insight.min.js',
      async: true,
    });
  }, [marketing, tokenPage]);

  useEffect(() => {
    if (!marketing || tokenPage || typeof window === 'undefined') return;

    if (!window.fbq) {
      const fbq = function fbq(...args) {
        if (fbq.callMethod) fbq.callMethod(...args);
        else fbq.queue.push(args);
      };
      fbq.push = fbq;
      fbq.loaded = true;
      fbq.version = '2.0';
      fbq.queue = [];
      window.fbq = fbq;
      window._fbq = fbq;
    }

    if (!window._tcMetaPixelInitialized) {
      window.fbq('init', META_PIXEL_ID);
      window._tcMetaPixelInitialized = true;
    }
    injectScript('meta-pixel', {
      src: 'https://connect.facebook.net/en_US/fbevents.js',
      async: true,
    });

    // Next.js route changes do not reload the base pixel, so explicitly record
    // one PageView per route while consent remains granted.
    if (lastMetaPageView.current !== asPath) {
      window.fbq('track', 'PageView');
      lastMetaPageView.current = asPath;
    }
  }, [asPath, marketing, tokenPage]);

  return null;
}
